import React, { useEffect, useState } from 'react';
import { productService } from '../services/api';
import { toast } from 'react-toastify';
import Table from '../components/Table';
import Button from '../components/Button';
import Modal from '../components/Modal';
import Input from '../components/Input';
import { FiPlus, FiSearch, FiEdit, FiTrash2 } from 'react-icons/fi';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    category: '',
    sellingPrice: '',
    costPrice: '',
    inventory: { quantity: 0, trackInventory: true, lowStockThreshold: 10 },
    description: '',
    barcode: '',
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await productService.getAll({ search });
      setProducts(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await productService.update(currentProduct._id, formData);
        toast.success('Product updated successfully');
      } else {
        await productService.create(formData);
        toast.success('Product created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save product');
    }
  };

  const handleEdit = (product) => {
    setCurrentProduct(product);
    setFormData({
      sku: product.sku,
      name: product.name,
      category: product.category,
      sellingPrice: product.sellingPrice,
      costPrice: product.costPrice,
      inventory: product.inventory,
      description: product.description || '',
      barcode: product.barcode || '',
    });
    setEditMode(true);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await productService.delete(id);
        toast.success('Product deleted successfully');
        fetchProducts();
      } catch (error) {
        toast.error('Failed to delete product');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      sku: '',
      name: '',
      category: '',
      sellingPrice: '',
      costPrice: '',
      inventory: { quantity: 0, trackInventory: true, lowStockThreshold: 10 },
      description: '',
      barcode: '',
    });
    setEditMode(false);
    setCurrentProduct(null);
  };

  const columns = [
    { header: 'SKU', accessor: 'sku' },
    { header: 'Name', accessor: 'name' },
    { header: 'Category', accessor: 'category' },
    {
      header: 'Price',
      render: (row) => `$${row.sellingPrice.toFixed(2)}`,
    },
    {
      header: 'Stock',
      render: (row) => (
        <span
          className={
            row.inventory.quantity <= row.inventory.lowStockThreshold
              ? 'text-red-600 font-semibold'
              : ''
          }
        >
          {row.inventory.quantity}
        </span>
      ),
    },
    {
      header: 'Status',
      render: (row) => (
        <span
          className={`px-2 py-1 text-xs rounded-full ${
            row.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {row.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      header: 'Actions',
      render: (row) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleEdit(row)}
            className="text-purple-600 hover:text-purple-800"
          >
            <FiEdit size={18} />
          </button>
          <button
            onClick={() => handleDelete(row._id)}
            className="text-red-600 hover:text-red-800"
          >
            <FiTrash2 size={18} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Products</h1>
        <Button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
        >
          <FiPlus className="inline mr-2" /> Add Product
        </Button>
      </div>

      <div className="mb-4 flex items-center">
        <div className="relative flex-1 max-w-md">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && fetchProducts()}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <Button onClick={fetchProducts} className="ml-2">
          Search
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      ) : (
        <Table columns={columns} data={products} />
      )}

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        title={editMode ? 'Edit Product' : 'Add New Product'}
        size="lg"
      >
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="SKU"
              name="sku"
              value={formData.sku}
              onChange={(e) => setFormData({ ...formData, sku: e.target.value.toUpperCase() })}
              required
              disabled={editMode}
            />
            <Input
              label="Product Name"
              name="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <Input
              label="Category"
              name="category"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              required
            />
            <Input
              label="Barcode"
              name="barcode"
              value={formData.barcode}
              onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
            />
            <Input
              label="Selling Price"
              type="number"
              step="0.01"
              name="sellingPrice"
              value={formData.sellingPrice}
              onChange={(e) => setFormData({ ...formData, sellingPrice: e.target.value })}
              required
            />
            <Input
              label="Cost Price"
              type="number"
              step="0.01"
              name="costPrice"
              value={formData.costPrice}
              onChange={(e) => setFormData({ ...formData, costPrice: e.target.value })}
            />
          </div>

          <Input
            label="Description"
            name="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />

          <h3 className="text-lg font-semibold mt-6 mb-4">Inventory</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Stock Quantity"
              type="number"
              name="quantity"
              value={formData.inventory.quantity}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  inventory: {
                    ...formData.inventory,
                    quantity: parseInt(e.target.value) || 0,
                  },
                })
              }
              required
            />
            <Input
              label="Low Stock Alert"
              type="number"
              name="lowStockThreshold"
              value={formData.inventory.lowStockThreshold}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  inventory: {
                    ...formData.inventory,
                    lowStockThreshold: parseInt(e.target.value) || 10,
                  },
                })
              }
            />
          </div>

          <div className="flex items-center mt-4">
            <input
              type="checkbox"
              id="trackInventory"
              checked={formData.inventory.trackInventory}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  inventory: { ...formData.inventory, trackInventory: e.target.checked },
                })
              }
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <label htmlFor="trackInventory" className="ml-2 text-sm text-gray-700">
              Track Inventory
            </label>
          </div>

          <div className="flex space-x-2 mt-6">
            <Button type="submit" className="flex-1">
              {editMode ? 'Update' : 'Create'} Product
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowModal(false);
                resetForm();
              }}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Products;
